import { HttpClient } from '../api/client'
import { deduplicateRequest } from '../api/deduplicateRequest'
import { AuthGuard } from '../auth/authGuard'
import type {
  ApiResponse,
  IntegrationEndpoints,
  IntegrationPlatform,
  TrelloConfig,
  JiraConfig,
  AzureDevOpsConfig,
} from '@bandos/shared'

const dedupedList = deduplicateRequest(
  async () => {
    const auth = await AuthGuard.getAuthHeadersOrError()
    if (auth.error) {
      return { data: null, error: auth.error } as ApiResponse<
        IntegrationEndpoints['list']['response']
      >
    }
    return HttpClient.get<IntegrationEndpoints['list']['response']>('/integrations', auth.headers)
  },
  { keyFn: () => 'integrations' }
)

/**
 * Integration service for handling all integration-related operations
 * Manages connections to external services (Trello, Jira, Azure DevOps)
 */
export class IntegrationService {
  /**
   * Get all integrations for the current user
   */
  static list(): Promise<ApiResponse<IntegrationEndpoints['list']['response']>> {
    return dedupedList()
  }

  /**
   * Get a specific integration for the current user
   * @param platform - Integration platform identifier
   */
  static async get(
    platform: IntegrationPlatform
  ): Promise<ApiResponse<IntegrationEndpoints['get']['response']>> {
    const auth = await AuthGuard.getAuthHeadersOrError()
    if (auth.error) {
      return { data: null, error: auth.error }
    }

    return HttpClient.get<IntegrationEndpoints['get']['response']>(
      `/integrations/${platform}`,
      auth.headers
    )
  }

  /**
   * Connect/create an integration
   * @param platform - Integration platform identifier
   * @param config - Platform-specific configuration
   */
  static async connect(
    platform: IntegrationPlatform,
    config: TrelloConfig | JiraConfig | AzureDevOpsConfig | Record<string, unknown>
  ): Promise<ApiResponse<IntegrationEndpoints['connect']['response']>> {
    const auth = await AuthGuard.getAuthHeadersOrError()
    if (auth.error) {
      return { data: null, error: auth.error }
    }

    const requestData: IntegrationEndpoints['connect']['body'] = {
      platform,
      config,
    }

    return HttpClient.post<IntegrationEndpoints['connect']['response']>(
      '/integrations/connect',
      requestData,
      auth.headers
    )
  }

  /**
   * Delete/disconnect an integration
   * @param platform - Integration platform identifier
   */
  static async disconnect(
    platform: IntegrationPlatform
  ): Promise<ApiResponse<{ success: boolean; message: string }>> {
    const auth = await AuthGuard.getAuthHeadersOrError()
    if (auth.error) {
      return { data: null, error: auth.error }
    }

    return HttpClient.delete<{ success: boolean; message: string }>(
      `/integrations/${platform}`,
      auth.headers
    )
  }

  /**
   * Send a requirement node to an integration
   * @param platform - Integration platform identifier
   * @param nodeId - ID of the requirement node to send
   * @param mindmapId - ID of the mindmap containing the node
   */
  static async sendRequirement(
    platform: IntegrationPlatform,
    nodeId: string,
    mindmapId: string
  ): Promise<ApiResponse<IntegrationEndpoints['send']['response']>> {
    const auth = await AuthGuard.getAuthHeadersOrError()
    if (auth.error) {
      return { data: null, error: auth.error }
    }

    const requestData: IntegrationEndpoints['send']['body'] = {
      platform,
      nodeId,
      mindmapId,
    }

    return HttpClient.post<IntegrationEndpoints['send']['response']>(
      '/integrations/send',
      requestData,
      auth.headers
    )
  }

  /**
   * Validate integration connection credentials
   * @param platform - Integration platform identifier
   * @param config - Platform-specific configuration to validate
   */
  static async validate(
    platform: IntegrationPlatform,
    config: TrelloConfig | JiraConfig | AzureDevOpsConfig | Record<string, unknown>
  ): Promise<ApiResponse<IntegrationEndpoints['validate']['response']>> {
    const auth = await AuthGuard.getAuthHeadersOrError()
    if (auth.error) {
      return { data: null, error: auth.error }
    }

    const requestData: IntegrationEndpoints['validate']['body'] = {
      platform,
      config,
    }

    return HttpClient.post<IntegrationEndpoints['validate']['response']>(
      '/integrations/validate',
      requestData,
      auth.headers
    )
  }

  /**
   * Get Trello OAuth 2.0 authorization URL
   * @param redirectUri - Callback URL where Trello will redirect after authorization
   */
  static async getTrelloAuthorizationUrl(
    redirectUri: string
  ): Promise<ApiResponse<{ authorizationUrl: string }>> {
    const auth = await AuthGuard.getAuthHeadersOrError()
    if (auth.error) {
      return { data: null, error: auth.error }
    }

    const queryParams = `?redirectUri=${encodeURIComponent(redirectUri)}`
    return HttpClient.get<{ authorizationUrl: string }>(
      `/integrations/trello/authorize${queryParams}`,
      auth.headers
    )
  }

  /**
   * Get Trello boards for the current user
   */
  static async getTrelloBoards(): Promise<
    ApiResponse<Array<{ id: string; name: string; url: string }>>
  > {
    const auth = await AuthGuard.getAuthHeadersOrError()
    if (auth.error) {
      return { data: null, error: auth.error }
    }

    return HttpClient.get<Array<{ id: string; name: string; url: string }>>(
      '/integrations/trello/boards',
      auth.headers
    )
  }

  /**
   * Get Trello lists (columns) for a specific board
   * @param boardId - Trello board ID
   */
  static async getTrelloLists(
    boardId: string
  ): Promise<ApiResponse<Array<{ id: string; name: string }>>> {
    const auth = await AuthGuard.getAuthHeadersOrError()
    if (auth.error) {
      return { data: null, error: auth.error }
    }

    return HttpClient.get<Array<{ id: string; name: string }>>(
      `/integrations/trello/boards/${encodeURIComponent(boardId)}/lists`,
      auth.headers
    )
  }

  /**
   * Get Jira OAuth 2.0 authorization URL
   * @param redirectUri - Callback URL where Jira will redirect after authorization
   */
  static async getJiraAuthorizationUrl(
    redirectUri: string
  ): Promise<ApiResponse<{ authorizationUrl: string }>> {
    const auth = await AuthGuard.getAuthHeadersOrError()
    if (auth.error) {
      return { data: null, error: auth.error }
    }

    const queryParams = `?redirectUri=${encodeURIComponent(redirectUri)}`
    return HttpClient.get<{ authorizationUrl: string }>(
      `/integrations/jira/authorize${queryParams}`,
      auth.headers
    )
  }

  /**
   * Get Jira projects for the current user (Core API - works for ALL Jira types)
   */
  static async getJiraProjects(): Promise<
    ApiResponse<Array<{ id: string; key: string; name: string }>>
  > {
    const auth = await AuthGuard.getAuthHeadersOrError()
    if (auth.error) {
      return { data: null, error: auth.error }
    }

    return HttpClient.get<Array<{ id: string; key: string; name: string }>>(
      '/integrations/jira/projects',
      auth.headers
    )
  }

  /**
   * Get Jira project statuses for a specific project (Core API - works for ALL Jira types)
   * @param projectKey - Jira project key (e.g., "PROJ")
   */
  static async getJiraProjectStatuses(
    projectKey: string
  ): Promise<ApiResponse<Array<{ id: string; name: string }>>> {
    const auth = await AuthGuard.getAuthHeadersOrError()
    if (auth.error) {
      return { data: null, error: auth.error }
    }

    return HttpClient.get<Array<{ id: string; name: string }>>(
      `/integrations/jira/projects/${encodeURIComponent(projectKey)}/statuses`,
      auth.headers
    )
  }
}
